import React from "react";
import PropTypes from "prop-types";

import useSheetStack from "../common/useSheetStack";
import SheetStack from "../common/SheetStack";
import { DX_V2_ADD, DX_V2_STATUS } from "./events";
import DiagnosisForm from "./DiagnosisForm";

/**
 * Diagnosis editor rail — "Editor · A4 filled (Diagnosis)" (2961:4745, 340
 * wide), now a STACK of write sheets, newest on top.
 *
 * All of the stack behaviour (the blank-sheet guard, one-sheet-per-record,
 * serialised saves, the refusal pulse, the live region, RAIL_OPEN/RAIL_CLOSE)
 * lives in ../common/useSheetStack + ../common/SheetStack, which every v3 rail
 * shares. This file is now only the section's wiring: which events open a
 * sheet, how to pull a diagnosis out of a payload, and what to call each sheet.
 *
 * The section has exactly TWO channels and there is no DX_V2_EDIT — the status
 * channel carries the record:
 *   DX_V2_ADD     the toolbar's "+ Add diagnosis" → a blank add sheet.
 *   DX_V2_STATUS  a row's treatment-status chip (active AND past tables) →
 *                 an "Update · <name>" sheet seeded from `{ diagnosis }`.
 * DX_V2_REFRESH is not listened to here at all: the rail emits it, and
 * Diagnosis.js (the content column) is the only listener.
 */

const ADD = [DX_V2_ADD];
const EDIT = [DX_V2_STATUS];

const getRecord = (payload) => payload && payload.diagnosis;
const getRecordId = (d) => d && d.diagnosisId;
const dupMessage = (d) => `${d.diagnosis} is already open for update.`;

const DiagnosisEditor = ({ patient }) => {
  const patientId = patient && (patient.id || patient.patientID);

  const stack = useSheetStack({
    sectionKey: "diagnosis",
    addEvents: ADD,
    editEvents: EDIT,
    getRecord,
    getRecordId,
    blankMessage: "A blank diagnosis form is already open.",
    dupMessage,
    enabled: !!patientId,
  });

  if (!patientId) {
    return <div className="ph-slot-empty is-editor"><p>Waiting for patient…</p></div>;
  }

  return (
    <SheetStack
      stack={stack}
      className="cc3-editor"
      describe={(s) => (s.editing ? `Update ${s.editing.diagnosis}` : "Add diagnosis")}
    >
      {(sheet, props) => <DiagnosisForm patientId={patientId} {...props} />}
    </SheetStack>
  );
};

DiagnosisEditor.propTypes = {
  patient: PropTypes.object,
};

export default DiagnosisEditor;
